import { defaultCrudMessages } from "../../shared/crud/simple-crud";

export const messages = {
  ...defaultCrudMessages,
  entityName: {
    id: "orchestrator.experiment.pageTitle",
    defaultMessage: "A/B Test – Scenario Comparator",
  },
  create: {
    id: "orchestrator.experiment.create",
    defaultMessage: "+ Create new",
  },
  edit: {
    id: "orchestrator.experiment.edit",
    defaultMessage: "Edit Experiment",
  },
  delete: {
    id: "orchestrator.experiment.delete",
    defaultMessage: "Delete",
  },
  back: {
    id: "orchestrator.experiment.back",
    defaultMessage: "Back",
  },
  tableHeaders: {
    ticker: {
      id: "orchestrator.experiment.header.ticker",
      defaultMessage: "Experiment",
    },
    status: {
      id: "orchestrator.experiment.header.status",
      defaultMessage: "Status",
    },
    variants: {
      id: "orchestrator.experiment.header.variants",
      defaultMessage: "Scenarios",
    },
    lift: {
      id: "orchestrator.experiment.header.lift",
      defaultMessage: "Current lift",
    },
    // CrudList resolves a column's header by the COLUMN key, so these two are named after the
    // `startedAt` / `estimatedEndDate` columns in index.tsx. Their ids stay as they are: that is
    // where the translated copy already lives.
    startedAt: {
      id: "orchestrator.experiment.header.startDate",
      defaultMessage: "Start",
    },
    estimatedEndDate: {
      id: "orchestrator.experiment.header.estimatedEndDate",
      defaultMessage: "End",
    },
    activeDays: {
      id: "orchestrator.experiment.header.activeDays",
      defaultMessage: "Duration",
    },
  },
  formLabels: {
    ticker: {
      id: "orchestrator.experiment.form.ticker",
      defaultMessage: "Experiment name",
    },
    tickerPlaceholder: {
      id: "orchestrator.experiment.form.tickerPlaceholder",
      defaultMessage: "e.g.: mobile_cta_test",
    },
    tickerHelp: {
      id: "orchestrator.experiment.form.tickerHelp",
      defaultMessage: "Must be a unique identifier, with no spaces or special characters.",
    },
    description: {
      id: "orchestrator.experiment.form.description",
      defaultMessage: "Description",
    },
    descriptionPlaceholder: {
      id: "orchestrator.experiment.form.descriptionPlaceholder",
      defaultMessage: "Describe the goal of the experiment",
    },
    controlVariant: {
      id: "orchestrator.experiment.form.controlVariant",
      defaultMessage: "Scenario A · Control",
    },
    testVariant: {
      id: "orchestrator.experiment.form.testVariant",
      defaultMessage: "Scenario B · Variant",
    },
    scenario: {
      id: "orchestrator.experiment.form.scenario",
      defaultMessage: "Multi-config",
    },
    selectScenarioPlaceholder: {
      id: "orchestrator.experiment.form.selectScenario",
      defaultMessage: "Select a scenario...",
    },
    defaultOption: {
      id: "orchestrator.experiment.form.defaultOption",
      defaultMessage: "Default — Engine base configuration",
    },
    noComparableScenarios: {
      id: "orchestrator.experiment.form.noComparableScenarios",
      defaultMessage:
        "No comparable scenario is available on this side for the current selection on the other side.",
    },
    scenarioName: {
      id: "orchestrator.experiment.form.scenarioName",
      defaultMessage: "Scenario name",
    },
    scenarioNameHelp: {
      id: "orchestrator.experiment.form.scenarioNameHelp",
      defaultMessage: "Name that will identify this scenario in the results.",
    },
    scenarioNamePlaceholderA: {
      id: "orchestrator.experiment.form.scenarioNamePlaceholderA",
      defaultMessage: "e.g.: Engine without Display",
    },
    scenarioNamePlaceholderB: {
      id: "orchestrator.experiment.form.scenarioNamePlaceholderB",
      defaultMessage: "e.g.: Engine with Display",
    },
    // The traffic split is fixed at 50/50 and no longer configurable, so this block is only about
    // when the test runs and where it applies. The key is kept (its copy, not its meaning, moved).
    trafficBlock: {
      id: "orchestrator.experiment.form.trafficBlock",
      defaultMessage: "Duration and scope",
    },
    startDate: {
      id: "orchestrator.experiment.form.startDate",
      defaultMessage: "Start date",
    },
    endDate: {
      id: "orchestrator.experiment.form.endDate",
      defaultMessage: "Estimated end date (optional)",
    },
    endDateHelp: {
      id: "orchestrator.experiment.form.endDateHelp",
      defaultMessage:
        "Recommended minimum: 14 days. Without an end date, the test is finished manually.",
    },
    targetPath: {
      id: "orchestrator.experiment.form.targetPath",
      defaultMessage: "Target path",
    },
    targetPathHelp: {
      id: "orchestrator.experiment.form.targetPathHelp",
      defaultMessage: "Path where the experiment applies. Use / for the whole booking engine.",
    },
    mde: {
      id: "orchestrator.experiment.form.mde",
      defaultMessage: "Minimum detectable effect (%)",
    },
    mdeHelp: {
      id: "orchestrator.experiment.form.mdeHelp",
      defaultMessage:
        "The smallest relative improvement you want to be able to detect. The smaller it is, the more visitors the test needs.",
    },
    minDays: {
      id: "orchestrator.experiment.form.minDays",
      defaultMessage: "Minimum duration (days)",
    },
    minDaysHelp: {
      id: "orchestrator.experiment.form.minDaysHelp",
      defaultMessage:
        "No winner is declared before this many days, however large the sample gets, so that a full weekly cycle is covered.",
    },
    sampleHint: {
      id: "orchestrator.experiment.form.sampleHint",
      defaultMessage:
        "You will need roughly {visitors} valid visitors (estimate based on an average {baseline} % conversion).",
    },
    submit: {
      id: "orchestrator.experiment.form.submit",
      defaultMessage: "Launch experiment",
    },
    cancel: {
      id: "orchestrator.experiment.form.cancel",
      defaultMessage: "Cancel",
    },
  },
  validation: {
    tickerRequired: {
      id: "orchestrator.experiment.validation.tickerRequired",
      defaultMessage: "⚠️ The ticker is required.",
    },
    tickerPattern: {
      id: "orchestrator.experiment.validation.tickerPattern",
      defaultMessage: "Ticker must be alphanumeric with underscores",
    },
    scenariosRequired: {
      id: "orchestrator.experiment.validation.scenariosRequired",
      defaultMessage: "⚠️ You must select two scenarios to create the experiment.",
    },
    scenarioRequired: {
      id: "orchestrator.experiment.validation.scenarioRequired",
      defaultMessage: "⚠️ You must select a scenario.",
    },
    scenarioInUse: {
      id: "orchestrator.experiment.validation.scenarioInUse",
      defaultMessage: "This scenario is already used in another experiment",
    },
    startDateRequired: {
      id: "orchestrator.experiment.validation.startDateRequired",
      defaultMessage: "⚠️ The start date is required.",
    },
    startDatePast: {
      id: "orchestrator.experiment.validation.startDatePast",
      defaultMessage: "⚠️ The start date cannot be earlier than today.",
    },
    endBeforeStart: {
      id: "orchestrator.experiment.validation.endBeforeStart",
      defaultMessage: "⚠️ The end date cannot be earlier than the start date.",
    },
    comparabilityOnlyInA: {
      id: "orchestrator.experiment.validation.comparability.onlyInA",
      defaultMessage: "{label} is only in Scenario A",
    },
    comparabilityOnlyInB: {
      id: "orchestrator.experiment.validation.comparability.onlyInB",
      defaultMessage: "{label} is only in Scenario B",
    },
    comparabilityDifferentValue: {
      id: "orchestrator.experiment.validation.comparability.differentValue",
      defaultMessage: "{label} differs between the scenarios",
    },
    comparabilityMatchModeDiffers: {
      id: "orchestrator.experiment.validation.comparability.matchModeDiffers",
      defaultMessage: "The match mode (All/Any) differs between the scenarios",
    },
  },
  errors: {
    tickerAlreadyExists: {
      id: "orchestrator.experiment.errors.tickerAlreadyExists",
      defaultMessage: "An experiment with this name already exists in this property.",
    },
    scenarioNotFound: {
      id: "orchestrator.experiment.errors.scenarioNotFound",
      defaultMessage:
        "One of the selected scenarios is no longer available. Reload the page and try again.",
    },
    genericSubmit: {
      id: "orchestrator.experiment.errors.genericSubmit",
      defaultMessage: "The experiment could not be created. Check the data and try again.",
    },
  },
  viewResults: {
    id: "orchestrator.experiment.viewResults",
    defaultMessage: "View Results",
  },
  newExperiment: {
    id: "orchestrator.experiment.newExperiment",
    defaultMessage: "New Experiment Configuration",
  },
  basicData: {
    id: "orchestrator.experiment.basicData",
    defaultMessage: "Identify the experiment",
  },
  scenarioSelection: {
    id: "orchestrator.experiment.scenarioSelection",
    defaultMessage: "Scenario selection",
  },
  scenarioSelectionHelp: {
    id: "orchestrator.experiment.form.scenariosHelp",
    defaultMessage:
      "Choose the scenario for each side and give it a name. Each scenario's conditions are defined in the Scenario Manager.",
  },
  saveAndActivate: {
    id: "orchestrator.experiment.saveAndActivate",
    defaultMessage: "Save and Activate",
  },
  status: {
    unspecified: {
      id: "orchestrator.experiment.status.unspecified",
      defaultMessage: "Unspecified",
    },
    draft: {
      id: "orchestrator.experiment.status.draft",
      defaultMessage: "Draft",
    },
    running: {
      id: "orchestrator.experiment.status.running",
      defaultMessage: "Running",
    },
    paused: {
      id: "orchestrator.experiment.status.paused",
      defaultMessage: "Paused",
    },
    archived: {
      id: "orchestrator.experiment.status.archived",
      defaultMessage: "Archived",
    },
  },
  results: {
    title: {
      id: "orchestrator.experiment.results.title",
      defaultMessage: "Results",
    },
    backToList: {
      id: "orchestrator.experiment.results.backToList",
      defaultMessage: "Back to list",
    },
    resultsTitle: {
      id: "orchestrator.experiment.results.resultsTitle",
      defaultMessage: "Results: {ticker}",
    },
    pauseExperiment: {
      id: "orchestrator.experiment.results.pauseExperiment",
      defaultMessage: "Pause Experiment",
    },
    declareWinner: {
      id: "orchestrator.experiment.results.declareWinner",
      defaultMessage: "Declare Winner",
    },
    resumeExperiment: {
      id: "orchestrator.experiment.results.resumeExperiment",
      defaultMessage: "Resume Experiment",
    },
    startExperiment: {
      id: "orchestrator.experiment.results.startExperiment",
      defaultMessage: "Start Experiment",
    },
    deleteExperiment: {
      id: "orchestrator.experiment.results.deleteExperiment",
      defaultMessage: "Delete",
    },
    confirmDelete: {
      id: "orchestrator.experiment.results.confirmDelete",
      defaultMessage: "Are you sure you want to delete this experiment?",
    },
    copiedToClipboard: {
      id: "orchestrator.experiment.results.copiedToClipboard",
      defaultMessage: "Copied to clipboard!",
    },
    loading: {
      id: "orchestrator.experiment.results.loading",
      defaultMessage: "Loading...",
    },
    notFoundTitle: {
      id: "orchestrator.experiment.results.notFoundTitle",
      defaultMessage: "Experiment not found",
    },
    trackingTags: {
      id: "orchestrator.experiment.results.trackingTags",
      defaultMessage: "System Tracking Tags",
    },
    errorTitle: {
      id: "orchestrator.experiment.results.errorTitle",
      defaultMessage: "The experiment could not be loaded",
    },
    pause: {
      id: "orchestrator.experiment.results.pause",
      defaultMessage: "Pause",
    },
    resume: {
      id: "orchestrator.experiment.results.resume",
      defaultMessage: "Resume",
    },
    finishTest: {
      id: "orchestrator.experiment.results.finishTest",
      defaultMessage: "Finish test",
    },
    // Title of the visible partial-data banner (previously a dead key: the condition only ever
    // reached a console.warn).
    dataIncomplete: {
      id: "orchestrator.experiment.results.dataIncomplete",
      defaultMessage: "Some of this experiment's data may be temporarily incomplete.",
    },
    // Provenance chip. The cut-off day is no longer a chip: it is a labelled value inside the
    // header's time-line block (`header.timelineDataUntil`). Neither is the configured split: it is
    // fixed at 50/50 and no longer surfaced.
    sourceWarehouse: {
      id: "orchestrator.experiment.results.sourceWarehouse",
      defaultMessage: "Source: warehouse",
    },
    sourceApi: {
      id: "orchestrator.experiment.results.sourceApi",
      defaultMessage: "Source: witbooking-api",
    },
    sourceFake: {
      id: "orchestrator.experiment.results.sourceFake",
      defaultMessage: "Source: test data",
    },
    sourceUnknown: {
      id: "orchestrator.experiment.results.sourceUnknown",
      defaultMessage: "Source: {source}",
    },
    // Partial-data warning codes. `unknown` deliberately echoes the raw code so an unrecognised
    // warning is still actionable instead of silently generic.
    warnings: {
      warehouseUnavailable: {
        id: "orchestrator.experiment.results.warnings.warehouseUnavailable",
        defaultMessage:
          "The data warehouse could not be reached, so these figures are not up to date.",
      },
      metricsUnavailable: {
        id: "orchestrator.experiment.results.warnings.metricsUnavailable",
        defaultMessage: "Visit metrics could not be loaded; only booking figures are reliable.",
      },
      multiCurrency: {
        id: "orchestrator.experiment.results.warnings.multiCurrency",
        defaultMessage:
          "This experiment has bookings in more than one currency; revenue figures mix currencies.",
      },
      // DATA_UNTIL_YESTERDAY is deliberately NOT listed here any more: it never degrades the
      // numbers, it explains their cut-off, so it now reads as the decision panel's footer note
      // (`decision.freshnessNote`) and this banner filters the code out entirely.
      unknown: {
        id: "orchestrator.experiment.results.warnings.unknown",
        defaultMessage: "There is a warning about this data ({code}).",
      },
    },
    actionFailed: {
      id: "orchestrator.experiment.results.actionFailed",
      defaultMessage: "The action could not be completed. Please try again.",
    },
    // The header's right-hand time-line block: three label/value pairs plus the scenario chips.
    // It replaces both the old free-text `meta*` line ("16 Ene 2026 – en curso · 145 días") and
    // <RunningDaysChip>'s `runningDaysChip.*` copy, whose information now lives here and in the
    // health strip.
    header: {
      timelineStart: {
        id: "orchestrator.experiment.results.header.timelineStart",
        defaultMessage: "Start",
      },
      // Deliberately NOT "Running": that is already the status badge's label, and two identical
      // strings in one header read as a duplicate rather than as a measurement.
      timelineRunning: {
        id: "orchestrator.experiment.results.header.timelineRunning",
        defaultMessage: "Running time",
      },
      timelineDataUntil: {
        id: "orchestrator.experiment.results.header.timelineDataUntil",
        defaultMessage: "Data up to",
      },
      timelineDaysValue: {
        id: "orchestrator.experiment.results.header.timelineDaysValue",
        defaultMessage: "{activeDays} of {minDays} days",
      },
      // `minDays === 0` means "unset" on the wire, so there is no denominator to state.
      timelineDaysNoMin: {
        id: "orchestrator.experiment.results.header.timelineDaysNoMin",
        defaultMessage: "{activeDays} days",
      },
      timelineMinHint: {
        id: "orchestrator.experiment.results.header.timelineMinHint",
        defaultMessage: "minimum {minDays}",
      },
      timelineDataUntilValue: {
        id: "orchestrator.experiment.results.header.timelineDataUntilValue",
        defaultMessage: "{date} · {timeZone}",
      },
      // Used while the property configuration has not resolved: the day is the load-bearing half,
      // and the separator has nothing to separate it from yet.
      timelineDataUntilValueNoZone: {
        id: "orchestrator.experiment.results.header.timelineDataUntilValueNoZone",
        defaultMessage: "{date}",
      },
      scenarioChip: {
        id: "orchestrator.experiment.results.header.scenarioChip",
        defaultMessage: "{side} · {name}",
      },
    },
    // The health strip's four tiles — pure formatting of values the results payload already
    // carries, so it opens no query of its own beyond the de-duplicated shared one.
    health: {
      observedSplitTitle: {
        id: "orchestrator.experiment.results.health.observedSplitTitle",
        defaultMessage: "Observed split",
      },
      observedSplitValue: {
        id: "orchestrator.experiment.results.health.observedSplitValue",
        defaultMessage: "{a} % / {b} %",
      },
      // A bare "OK" reads as a system status rather than as an answer about the traffic: both
      // captions name what was actually checked.
      observedSplitOk: {
        id: "orchestrator.experiment.results.health.observedSplitOk",
        defaultMessage: "Balanced split",
      },
      observedSplitDrift: {
        id: "orchestrator.experiment.results.health.observedSplitDrift",
        defaultMessage: "Drift detected",
      },
      visitorsTitle: {
        id: "orchestrator.experiment.results.health.visitorsTitle",
        defaultMessage: "Valid visitors",
      },
      visitorsSub: {
        id: "orchestrator.experiment.results.health.visitorsSub",
        defaultMessage: "+{count} excluded: staff, previews and bots",
      },
      visitorsSubNone: {
        id: "orchestrator.experiment.results.health.visitorsSubNone",
        defaultMessage: "No visitor excluded",
      },
      paceTitle: {
        id: "orchestrator.experiment.results.health.paceTitle",
        defaultMessage: "Pace",
      },
      paceValue: {
        id: "orchestrator.experiment.results.health.paceValue",
        defaultMessage: "{visitors} / day",
      },
      paceSub: {
        id: "orchestrator.experiment.results.health.paceSub",
        defaultMessage: "Full sample in about {days} days",
      },
      paceSubUnknown: {
        id: "orchestrator.experiment.results.health.paceSubUnknown",
        defaultMessage: "Not enough data to estimate it",
      },
      durationTitle: {
        id: "orchestrator.experiment.results.health.durationTitle",
        defaultMessage: "Duration",
      },
      durationValue: {
        id: "orchestrator.experiment.results.health.durationValue",
        defaultMessage: "{activeDays} / {minDays} days",
      },
      durationValueNoMin: {
        id: "orchestrator.experiment.results.health.durationValueNoMin",
        defaultMessage: "{activeDays} days",
      },
      durationSubPending: {
        id: "orchestrator.experiment.results.health.durationSubPending",
        defaultMessage: "{days} days to cover the minimum duration",
      },
      durationSubMet: {
        id: "orchestrator.experiment.results.health.durationSubMet",
        defaultMessage: "Minimum covered",
      },
    },
    // `chartTitle`/`chartPlaceholder` removed by US-021: `components/ConversionRateChart.tsx` (the
    // only consumer) has been deleted and replaced by `ExperimentEvolutionChart.tsx`'s own
    // `resultsChart.*` copy — confirmed no remaining reference to either key.
    kpiDashboard: {
      id: "orchestrator.experiment.results.kpiDashboard",
      defaultMessage: "KPI Comparison Dashboard",
    },
    kpiSummary: {
      id: "orchestrator.experiment.results.kpiSummary",
      defaultMessage:
        "Currently, {variant} is outperforming the Control with a {percentage} increase in Conversion Rate.",
    },
    variantB: {
      id: "orchestrator.experiment.results.variantB",
      defaultMessage: "Variant B",
    },
    scenarioAInfo: {
      id: "orchestrator.experiment.results.scenarioAInfo",
      defaultMessage: "Scenario A (Control): {ticker}",
    },
    scenarioBInfo: {
      id: "orchestrator.experiment.results.scenarioBInfo",
      defaultMessage: "Scenario B (Variant): {ticker}",
    },
    scenarioAFallbackName: {
      id: "orchestrator.experiment.results.scenarioAFallbackName",
      defaultMessage: "Scenario A (Control)",
    },
    scenarioBFallbackName: {
      id: "orchestrator.experiment.results.scenarioBFallbackName",
      defaultMessage: "Scenario B (Variant)",
    },
    // The decision panel (<VerdictBanner>): a headline that answers "is there a winner?", a state
    // pill, one sentence of reason, and the backend's three gates as rows. `collectingNoTarget`,
    // `etaDaysLeft` and `srmDetail` keep their original `verdict.*`/`srm.*` ids — the copy is
    // unchanged, only its place on the screen is.
    decision: {
      headlineCollecting: {
        id: "orchestrator.experiment.results.decision.headlineCollecting",
        defaultMessage: "No winner yet",
      },
      headlineWinner: {
        id: "orchestrator.experiment.results.decision.headlineWinner",
        defaultMessage: "{side} wins: {name}",
      },
      headlineNoWinner: {
        id: "orchestrator.experiment.results.decision.headlineNoWinner",
        defaultMessage: "No conclusive difference",
      },
      headlineSrm: {
        id: "orchestrator.experiment.results.decision.headlineSrm",
        defaultMessage: "Traffic split has drifted",
      },
      pillCollecting: {
        id: "orchestrator.experiment.results.decision.pillCollecting",
        defaultMessage: "Collecting data",
      },
      pillWinner: {
        id: "orchestrator.experiment.results.decision.pillWinner",
        defaultMessage: "Winner",
      },
      pillNoWinner: {
        id: "orchestrator.experiment.results.decision.pillNoWinner",
        defaultMessage: "Not conclusive",
      },
      // One sentence, built from the data. `leader`/`other` are the scenario letters, so the
      // sentence reads against the A/B labelling the rest of the screen uses.
      reasonWinner: {
        id: "orchestrator.experiment.results.decision.reasonWinner",
        defaultMessage:
          "{winner} converts {lift} % more than {loser}, with {confidence} % confidence.",
      },
      reasonNoWinner: {
        id: "orchestrator.experiment.results.decision.reasonNoWinner",
        defaultMessage:
          "The observed difference between A and B is {lift} % with {confidence} % confidence, below the 95 % required to declare a winner.",
      },
      reasonCollecting: {
        id: "orchestrator.experiment.results.decision.reasonCollecting",
        defaultMessage:
          "{leader} converts {lift} % more than {other} with {confidence} % confidence, but the result is not reliable until the three conditions are met.",
      },
      reasonCollectingLowConfidence: {
        id: "orchestrator.experiment.results.decision.reasonCollectingLowConfidence",
        defaultMessage:
          "{leader} converts {lift} % more than {other}, but confidence is {confidence} % and 95 % is required to declare a winner.",
      },
      reasonCollectingFlat: {
        id: "orchestrator.experiment.results.decision.reasonCollectingFlat",
        defaultMessage:
          "There is no measurable difference between the two scenarios yet; the test is still collecting data.",
      },
      reasonSrm: {
        id: "orchestrator.experiment.results.srm.detail",
        defaultMessage:
          "Observed split {observedA}/{observedB} against the configured {expectedA}/{expectedB}. Review it before drawing conclusions.",
      },
      gatesTitle: {
        id: "orchestrator.experiment.results.decision.gatesTitle",
        defaultMessage: "Conditions to declare a winner",
      },
      gateSampleLabel: {
        id: "orchestrator.experiment.results.decision.gateSampleLabel",
        defaultMessage: "Planned sample",
      },
      gateSampleValue: {
        id: "orchestrator.experiment.results.decision.gateSampleValue",
        defaultMessage: "{collected} of {required} · {pct} %",
      },
      // Shown instead of the figure above when the backend could not size the test — including
      // the int64-sentinel `required_visitors` a warehouse-less response sends.
      collectingNoTarget: {
        id: "orchestrator.experiment.results.verdict.collectingNoTarget",
        defaultMessage: "{collected} valid visitors collected so far",
      },
      gateBookingsLabel: {
        id: "orchestrator.experiment.results.decision.gateBookingsLabel",
        defaultMessage: "At least {threshold} bookings per scenario",
      },
      gateBookingsValue: {
        id: "orchestrator.experiment.results.decision.gateBookingsValue",
        defaultMessage: "A {a} · B {b}",
      },
      gateDurationLabel: {
        id: "orchestrator.experiment.results.decision.gateDurationLabel",
        defaultMessage: "Minimum duration {minDays} days",
      },
      gateDurationValue: {
        id: "orchestrator.experiment.results.decision.gateDurationValue",
        defaultMessage: "{activeDays} of {minDays} days",
      },
      // `minDays === 0` means "unset" on the wire. There is no condition to state, and no
      // condition to tick off either — mirrors the header's own timelineDaysNoMin treatment.
      gateDurationLabelNoMin: {
        id: "orchestrator.experiment.results.decision.gateDurationLabelNoMin",
        defaultMessage: "Minimum duration",
      },
      gateDurationValueNoMin: {
        id: "orchestrator.experiment.results.decision.gateDurationValueNoMin",
        defaultMessage: "No minimum configured",
      },
      // Read out next to the status dot, so the met/pending distinction is never colour-only.
      gateMet: {
        id: "orchestrator.experiment.results.decision.gateMet",
        defaultMessage: "met",
      },
      gatePending: {
        id: "orchestrator.experiment.results.decision.gatePending",
        defaultMessage: "pending",
      },
      etaDaysLeft: {
        id: "orchestrator.experiment.results.verdict.collectingDaysLeft",
        defaultMessage: "About {days} days left at the current rate",
      },
      freshnessNote: {
        id: "orchestrator.experiment.results.decision.freshnessNote",
        defaultMessage: "Data goes up to yesterday: today's visits and bookings are not included.",
      },
    },
    // US-019: ScenarioComparisonCards' winner/reliability badges and inline loading/error copy.
    winner: {
      id: "orchestrator.experiment.results.winner",
      defaultMessage: "WINNER",
    },
    reliable: {
      id: "orchestrator.experiment.results.reliable",
      defaultMessage: "Reliable ✓",
    },
    // Last-resort badge: `is_reliable === false` while every gate this client can see is met.
    notReliable: {
      id: "orchestrator.experiment.results.notReliable",
      defaultMessage: "Not reliable",
    },
    // Replaces the old, uninformative "No fiable" badge in every case the response can explain:
    // the comparison's middle cell names WHICH of the decision panel's three gates is still open,
    // using the same derivation.
    pendingOf: {
      id: "orchestrator.experiment.results.pendingOf",
      defaultMessage: "Pending: {items}",
    },
    gateNounSample: {
      id: "orchestrator.experiment.results.gateNounSample",
      defaultMessage: "sample",
    },
    gateNounBookings: {
      id: "orchestrator.experiment.results.gateNounBookings",
      defaultMessage: "bookings",
    },
    gateNounDuration: {
      id: "orchestrator.experiment.results.gateNounDuration",
      defaultMessage: "duration",
    },
    liftLabel: {
      id: "orchestrator.experiment.results.liftLabel",
      defaultMessage: "Lift of B over A",
    },
    confidenceLine: {
      id: "orchestrator.experiment.results.confidenceLine",
      defaultMessage: "Confidence {confidence} % · a winner is declared from 95 %",
    },
    tableFootnote: {
      id: "orchestrator.experiment.results.tableFootnote",
      defaultMessage:
        "Confidence = 1 − p of the two-proportion z-test against the control. Only conversion is tested; the rest are descriptive.",
    },
    comparisonLoading: {
      id: "orchestrator.experiment.results.comparisonLoading",
      defaultMessage: "Loading comparison…",
    },
    comparisonError: {
      id: "orchestrator.experiment.results.comparisonError",
      defaultMessage: "The scenario comparison could not be loaded.",
    },
    exportMetrics: {
      id: "orchestrator.experiment.results.exportMetrics",
      defaultMessage: "Export metrics",
    },
    exportingMetrics: {
      id: "orchestrator.experiment.results.exportingMetrics",
      defaultMessage: "Exporting...",
    },
    // US-022: AllMetricsTable's title, table column headers, and footnotes.
    allMetrics: {
      id: "orchestrator.experiment.results.allMetrics",
      defaultMessage: "All metrics",
    },
    table: {
      metric: {
        id: "orchestrator.experiment.results.table.metric",
        defaultMessage: "Metric",
      },
      scenarioA: {
        id: "orchestrator.experiment.results.table.scenarioA",
        defaultMessage: "Scenario A · {name}",
      },
      scenarioB: {
        id: "orchestrator.experiment.results.table.scenarioB",
        defaultMessage: "Scenario B · {name}",
      },
      winner: {
        id: "orchestrator.experiment.results.table.winner",
        defaultMessage: "Winner",
      },
      confidence: {
        id: "orchestrator.experiment.results.table.confidence",
        defaultMessage: "Confidence",
      },
      observedSplit: {
        id: "orchestrator.experiment.results.table.observedSplit",
        defaultMessage: "{observedA} % / {observedB} %",
      },
      relativeDiff: {
        id: "orchestrator.experiment.results.table.relativeDiff",
        defaultMessage: "Relative difference",
      },
    },
    dataUnavailable: {
      id: "orchestrator.experiment.results.dataUnavailable",
      defaultMessage: "Data temporarily unavailable.",
    },
    // Bare "Scenario {side}" label (rendered with `text-uppercase` for the "ESCENARIO A/B" look) —
    // distinct from `table.scenarioA/B` ("Escenario A · {name}") and the dead
    // `scenarioAFallbackName`/`scenarioBFallbackName`/`kpiMetrics.scenarioAControl` keys.
    validVisitors: {
      id: "orchestrator.experiment.results.validVisitors",
      defaultMessage: "Valid visitors",
    },
    excludedVisitors: {
      id: "orchestrator.experiment.results.excludedVisitors",
      defaultMessage: "+{count} excluded",
    },
    excludedVisitorsTooltip: {
      id: "orchestrator.experiment.results.excludedVisitorsTooltip",
      defaultMessage:
        "{excluded} visitors excluded from the denominator (staff previews, operator traffic and bots), {bots} of them suspected bots.",
    },
    conversionInterval: {
      id: "orchestrator.experiment.results.conversionInterval",
      defaultMessage: "95 % interval {low} % – {high} %",
    },
    // Now a full sentence rather than a bare pair of bounds: it is the only 95 % interval on the
    // comparison row, so it names itself.
    liftInterval: {
      id: "orchestrator.experiment.results.liftInterval",
      defaultMessage: "95 % interval: {low} % to {high} %",
    },
    exportSeries: {
      id: "orchestrator.experiment.results.exportSeries",
      defaultMessage: "Export daily series",
    },
    segments: {
      title: {
        id: "orchestrator.experiment.results.segments.title",
        defaultMessage: "Segments",
      },
      dimensionSelector: {
        id: "orchestrator.experiment.results.segments.dimensionSelector",
        defaultMessage: "Breakdown",
      },
      dimensionDevice: {
        id: "orchestrator.experiment.results.segments.dimensionDevice",
        defaultMessage: "Device",
      },
      dimensionOriginClass: {
        id: "orchestrator.experiment.results.segments.dimensionOriginClass",
        defaultMessage: "Traffic origin",
      },
      columnSegment: {
        id: "orchestrator.experiment.results.segments.columnSegment",
        defaultMessage: "Segment",
      },
      columnVisitors: {
        id: "orchestrator.experiment.results.segments.columnVisitors",
        defaultMessage: "Valid visitors (A / B)",
      },
      columnConversion: {
        id: "orchestrator.experiment.results.segments.columnConversion",
        defaultMessage: "Conversion (A / B)",
      },
      columnLift: {
        id: "orchestrator.experiment.results.segments.columnLift",
        defaultMessage: "Lift",
      },
      columnConfidence: {
        id: "orchestrator.experiment.results.segments.columnConfidence",
        defaultMessage: "Confidence",
      },
      smallSample: {
        id: "orchestrator.experiment.results.segments.smallSample",
        defaultMessage: "small sample",
      },
      empty: {
        id: "orchestrator.experiment.results.segments.empty",
        defaultMessage:
          "There is no segment breakdown yet: it appears once the experiment has enough exposures per device and origin.",
      },
    },
    scenarioLabel: {
      id: "orchestrator.experiment.results.scenarioLabel",
      defaultMessage: "Scenario {side}",
    },
  },
  // US-019/US-021/US-022: metric labels shared by ScenarioComparisonCards, ExperimentEvolutionChart
  // and AllMetricsTable, reused from PLAN-03 §8.3/§8.4's already-enumerated i18n table (single
  // source of truth — a second, duplicate `metrics` object introduced by US-021's own branch was
  // consolidated here).
  metrics: {
    visits: {
      id: "orchestrator.experiment.metrics.visits",
      defaultMessage: "Visits",
    },
    conversionRate: {
      id: "orchestrator.experiment.metrics.conversionRate",
      defaultMessage: "Conversion rate",
    },
    bookings: {
      id: "orchestrator.experiment.metrics.bookings",
      defaultMessage: "Total bookings",
    },
    revenueTotal: {
      id: "orchestrator.experiment.metrics.revenueTotal",
      defaultMessage: "Total revenue",
    },
    revenuePerVisit: {
      id: "orchestrator.experiment.metrics.revenuePerVisit",
      defaultMessage: "Revenue per visit",
    },
    adr: {
      id: "orchestrator.experiment.metrics.adr",
      defaultMessage: "ADR (average price / night)",
    },
    avgBookingValue: {
      id: "orchestrator.experiment.metrics.avgBookingValue",
      defaultMessage: "Average booking value",
    },
    abandonmentRate: {
      id: "orchestrator.experiment.metrics.abandonmentRate",
      defaultMessage: "Abandonment rate",
    },
    desc: {
      visits: {
        id: "orchestrator.experiment.metrics.desc.visits",
        defaultMessage: "Number of visits per scenario.",
      },
      conversionRate: {
        id: "orchestrator.experiment.metrics.desc.conversionRate",
        defaultMessage: "% of visitors who complete a booking out of all engine visits.",
      },
      bookings: {
        id: "orchestrator.experiment.metrics.desc.bookings",
        defaultMessage:
          "Number of bookings recorded during the period: confirmed, cancelled and no-show. It measures visitor conversion, not net production.",
      },
      revenueTotal: {
        id: "orchestrator.experiment.metrics.desc.revenueTotal",
        defaultMessage:
          "Total amount of the experiment's bookings (confirmed, cancelled and no-show). This is not a billing figure: it does not match the business reports, which only count confirmed ones.",
      },
      revenuePerVisit: {
        id: "orchestrator.experiment.metrics.desc.revenuePerVisit",
        defaultMessage:
          "Average amount per engine visit, over the same set of bookings. A key metric for Revenue Management.",
      },
      adr: {
        id: "orchestrator.experiment.metrics.desc.adr",
        defaultMessage: "Average Daily Rate: average rate charged per room per night.",
      },
      avgBookingValue: {
        id: "orchestrator.experiment.metrics.desc.avgBookingValue",
        defaultMessage:
          "Average amount of each booking including all nights, over the same set of bookings.",
      },
      abandonmentRate: {
        id: "orchestrator.experiment.metrics.desc.abandonmentRate",
        defaultMessage: "% of users who start but do not complete the booking.",
      },
    },
  },
  export: {
    error: {
      id: "orchestrator.experiment.export.error",
      defaultMessage: "The export could not be generated. Try again.",
    },
    errorDraft: {
      id: "orchestrator.experiment.export.errorDraft",
      defaultMessage: "This experiment has not started yet, there are no metrics to export.",
    },
    bookings: {
      id: "orchestrator.experiment.export.bookings",
      defaultMessage: "Export booking details",
    },
  },
  confirm: {
    pauseTitle: {
      id: "orchestrator.experiment.confirm.pauseTitle",
      defaultMessage: "Pause the experiment?",
    },
    pauseText: {
      id: "orchestrator.experiment.confirm.pauseText",
      defaultMessage: "Traffic will stop being split between the variants while it is paused.",
    },
    resumeTitle: {
      id: "orchestrator.experiment.confirm.resumeTitle",
      defaultMessage: "Resume the experiment?",
    },
    resumeText: {
      id: "orchestrator.experiment.confirm.resumeText",
      defaultMessage: "The experiment will split traffic between the variants again.",
    },
    finishTitle: {
      id: "orchestrator.experiment.confirm.finishTitle",
      defaultMessage: "Finish the test?",
    },
    finishText: {
      id: "orchestrator.experiment.confirm.finishText",
      defaultMessage: "This action will archive the experiment and cannot be undone.",
    },
    accept: {
      id: "orchestrator.experiment.confirm.accept",
      defaultMessage: "Yes, continue",
    },
    cancel: {
      id: "orchestrator.experiment.confirm.cancel",
      defaultMessage: "Cancel",
    },
  },
  kpiMetrics: {
    metric: {
      id: "orchestrator.experiment.kpiMetrics.metric",
      defaultMessage: "METRIC",
    },
    scenarioAControl: {
      id: "orchestrator.experiment.kpiMetrics.scenarioAControl",
      defaultMessage: "SCENARIO A (CONTROL)",
    },
    scenarioBVariant: {
      id: "orchestrator.experiment.kpiMetrics.scenarioBVariant",
      defaultMessage: "SCENARIO B (VARIANT)",
    },
    difference: {
      id: "orchestrator.experiment.kpiMetrics.difference",
      defaultMessage: "DIFFERENCE (%)",
    },
    visits: {
      id: "orchestrator.experiment.kpiMetrics.visits",
      defaultMessage: "Visits",
    },
    totalReservations: {
      id: "orchestrator.experiment.kpiMetrics.totalReservations",
      defaultMessage: "Total bookings",
    },
    conversionRate: {
      id: "orchestrator.experiment.kpiMetrics.conversionRate",
      defaultMessage: "Conversion Rate (%)",
    },
    revenue: {
      id: "orchestrator.experiment.kpiMetrics.revenue",
      defaultMessage: "Revenue Generated (€)",
    },
    adr: {
      id: "orchestrator.experiment.kpiMetrics.adr",
      defaultMessage: "ADR (€)",
    },
  },
  filters: {
    title: { id: "orchestrator.experiment.filters.title", defaultMessage: "Filters" },
    namePlaceholder: {
      id: "orchestrator.experiment.filters.namePlaceholder",
      defaultMessage: "Search experiment name",
    },
    statusPlaceholder: {
      id: "orchestrator.experiment.filters.statusPlaceholder",
      defaultMessage: "Status",
    },
    statusAll: { id: "orchestrator.experiment.filters.statusAll", defaultMessage: "All" },
    statusIniciar: {
      id: "orchestrator.experiment.filters.statusIniciar",
      defaultMessage: "Start",
    },
    dateRangePlaceholder: {
      id: "orchestrator.experiment.filters.dateRangePlaceholder",
      defaultMessage: "Start",
    },
    minDurationPlaceholder: {
      id: "orchestrator.experiment.filters.minDurationPlaceholder",
      defaultMessage: "Minimum duration (days)",
    },
    clear: { id: "orchestrator.experiment.filters.clear", defaultMessage: "Clear filters" },
  },
  rowActions: {
    confirmAccept: {
      id: "orchestrator.experiment.rowActions.confirmAccept",
      defaultMessage: "Yes",
    },
    confirmCancel: {
      id: "orchestrator.experiment.rowActions.confirmCancel",
      defaultMessage: "Cancel",
    },
    start: {
      tooltip: {
        id: "orchestrator.experiment.rowActions.start.tooltip",
        defaultMessage: "Start",
      },
      success: {
        id: "orchestrator.experiment.rowActions.start.success",
        defaultMessage: "Experiment started",
      },
      error: {
        id: "orchestrator.experiment.rowActions.start.error",
        defaultMessage: "The experiment could not be started",
      },
    },
    pause: {
      tooltip: { id: "orchestrator.experiment.rowActions.pause.tooltip", defaultMessage: "Pause" },
      confirmTitle: {
        id: "orchestrator.experiment.rowActions.pause.confirmTitle",
        defaultMessage: "Pause the experiment?",
      },
      confirmText: {
        id: "orchestrator.experiment.rowActions.pause.confirmText",
        defaultMessage:
          "The experiment will stop assigning traffic to the scenarios. You can resume it later.",
      },
      success: {
        id: "orchestrator.experiment.rowActions.pause.success",
        defaultMessage: "Experiment paused",
      },
      error: {
        id: "orchestrator.experiment.rowActions.pause.error",
        defaultMessage: "The experiment could not be paused",
      },
    },
    resume: {
      tooltip: {
        id: "orchestrator.experiment.rowActions.resume.tooltip",
        defaultMessage: "Resume",
      },
      confirmTitle: {
        id: "orchestrator.experiment.rowActions.resume.confirmTitle",
        defaultMessage: "Resume the experiment?",
      },
      confirmText: {
        id: "orchestrator.experiment.rowActions.resume.confirmText",
        defaultMessage: "The experiment will split traffic between the two scenarios again.",
      },
      success: {
        id: "orchestrator.experiment.rowActions.resume.success",
        defaultMessage: "Experiment resumed",
      },
      error: {
        id: "orchestrator.experiment.rowActions.resume.error",
        defaultMessage: "The experiment could not be resumed",
      },
    },
    delete: {
      success: {
        id: "orchestrator.experiment.rowActions.delete.success",
        defaultMessage: "Experiment deleted",
      },
      error: {
        id: "orchestrator.experiment.rowActions.delete.error",
        defaultMessage: "The experiment could not be deleted",
      },
    },
  },
  common: {
    conditions: {
      id: "orchestrator.experiment.common.conditions",
      defaultMessage: "Conditions",
    },
    vs: {
      id: "orchestrator.experiment.common.vs",
      defaultMessage: "vs",
    },
    na: {
      id: "orchestrator.experiment.common.na",
      defaultMessage: "N/A",
    },
    variantA: {
      id: "orchestrator.experiment.common.variantA",
      defaultMessage: "A: {ticker}",
    },
    variantB: {
      id: "orchestrator.experiment.common.variantB",
      defaultMessage: "B: {ticker}",
    },
    durationDays: {
      id: "orchestrator.experiment.common.durationDays",
      defaultMessage: "{days} days",
    },
    // Joins the last two items of a short enumeration. `Intl.ListFormat` is not available in
    // every environment this bundle runs in (nor in this Jest setup), so the conjunction is a
    // translatable string rather than a runtime formatter.
    listConjunction: {
      id: "orchestrator.experiment.common.listConjunction",
      defaultMessage: "{first} and {last}",
    },
  },
  lift: {
    leadingA: {
      id: "orchestrator.experiment.lift.leadingA",
      defaultMessage: "A leads",
    },
    leadingB: {
      id: "orchestrator.experiment.lift.leadingB",
      defaultMessage: "B leads",
    },
    noWinner: {
      id: "orchestrator.experiment.lift.noWinner",
      defaultMessage: "No clear winner",
    },
    insufficientData: {
      id: "orchestrator.experiment.lift.insufficientData",
      defaultMessage: "Insufficient data",
    },
    sampleProgress: {
      id: "orchestrator.experiment.lift.sampleProgress",
      defaultMessage: "{pct} % of the sample",
    },
    subtitle: {
      id: "orchestrator.experiment.lift.subtitle",
      defaultMessage: "{leader} · {confidence}% confidence",
    },
  },
  resultsChart: {
    title: {
      id: "orchestrator.experiment.results.chartTitle",
      defaultMessage: "Cumulative evolution · {metric}",
    },
    noData: {
      id: "orchestrator.experiment.results.chartNoData",
      defaultMessage: "There is not enough data yet to show the evolution.",
    },
    draftPlaceholder: {
      id: "orchestrator.experiment.results.chartDraftPlaceholder",
      defaultMessage:
        "The evolution chart will be available once the experiment moves to 'Running'.",
    },
    comingSoon: {
      id: "orchestrator.experiment.results.chartRangeComingSoon",
      defaultMessage: "This range will be available soon. Try 'Custom date' in the meantime.",
    },
    metricSelector: {
      id: "orchestrator.experiment.results.metricSelector",
      defaultMessage: "Metric",
    },
    rangeSelector: {
      id: "orchestrator.experiment.results.rangeSelector",
      defaultMessage: "Range",
    },
    range: {
      last7: {
        id: "orchestrator.experiment.results.range.last7",
        defaultMessage: "Last 7 days",
      },
      last15: {
        id: "orchestrator.experiment.results.range.last15",
        defaultMessage: "Last 15 days",
      },
      last30: {
        id: "orchestrator.experiment.results.range.last30",
        defaultMessage: "Last 30 days",
      },
      full: { id: "orchestrator.experiment.results.range.full", defaultMessage: "Full range" },
      custom: {
        id: "orchestrator.experiment.results.range.custom",
        defaultMessage: "Custom date",
      },
    },
    week: { id: "orchestrator.experiment.results.week", defaultMessage: "Wk {n}" },
    granularityDay: {
      id: "orchestrator.experiment.results.granularityDay",
      defaultMessage: "Daily data",
    },
    granularityWeek: {
      id: "orchestrator.experiment.results.granularityWeek",
      defaultMessage: "Weekly data",
    },
    pauseMarker: {
      id: "orchestrator.experiment.results.pauseMarker",
      defaultMessage: "Paused",
    },
    dataUnavailable: {
      id: "orchestrator.experiment.results.dataUnavailable",
      defaultMessage: "Data temporarily unavailable.",
    },
  },
};
